import { useEffect, useState } from "react";
import { Calendar, MapPin, Clock } from "lucide-react";
import api from "../lib/api";
import { RECURRING_SERVICES, nextOccurrence } from "../lib/assets";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function CalendarPage() {
  const [adminEvents, setAdminEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setAdminEvents(r.data)).catch(() => {});
  }, []);

  // Build a 4-week rolling view of all recurring services + special events.
  const today = new Date();
  const weeks = [];
  for (let w = 0; w < 4; w++) {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + w * 7);
    const items = [];

    RECURRING_SERVICES.forEach((s) => {
      const d = nextOccurrence(s.weekday, weekStart);
      d.setHours(s.startHour, 0, 0, 0);
      items.push({
        ...s,
        date: d,
        kind: "recurring",
      });
    });

    // Special events that fall within this week
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);
    adminEvents.forEach((e) => {
      const d = new Date(e.event_date);
      if (d >= weekStart && d < weekEnd) {
        items.push({
          id: e.id,
          title: e.title,
          weekdayLabel: d.toLocaleDateString("es-ES", { weekday: "long" }),
          timeLabel: d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }),
          location: e.location,
          description: e.description,
          date: d,
          kind: "special",
        });
      }
    });

    items.sort((a, b) => a.date - b.date);
    weeks.push({ start: weekStart, items });
  }

  return (
    <div data-testid="calendar-page" className="pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--inv-text-muted)] mb-3">
          Programación · Servicios semanales
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
          Calendario
        </h1>
        <p className="mt-4 max-w-2xl text-[color:var(--inv-text-muted)]">
          Cultos regulares, estudios bíblicos y eventos especiales de la
          iglesia. ¡Te esperamos!
        </p>

        {/* Recurring summary */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {RECURRING_SERVICES.map((s) => (
            <div
              key={s.id}
              data-testid={`recurring-${s.id}`}
              className={`rounded-2xl p-6 border ${
                s.accent === "primary"
                  ? "bg-primary-warm text-white border-transparent"
                  : "bg-surface border-warm"
              }`}
            >
              <div
                className={`text-xs uppercase tracking-[0.18em] font-semibold mb-2 ${
                  s.accent === "primary" ? "text-white/80" : "text-[color:var(--inv-primary)]"
                }`}
              >
                Cada {s.weekdayLabel}
              </div>
              <h3 className="font-heading text-2xl font-semibold tracking-tight">
                {s.title}
              </h3>
              <div
                className={`mt-3 inline-flex items-center gap-1.5 text-sm ${
                  s.accent === "primary" ? "text-white/90" : "text-[color:var(--inv-text-muted)]"
                }`}
              >
                <Clock size={14} /> {s.timeLabel}
              </div>
              <div
                className={`mt-1 inline-flex items-center gap-1.5 text-sm ${
                  s.accent === "primary" ? "text-white/90" : "text-[color:var(--inv-text-muted)]"
                }`}
              >
                <MapPin size={14} /> {s.location}
              </div>
              <p
                className={`mt-3 text-sm leading-relaxed ${
                  s.accent === "primary" ? "text-white/85" : "text-[color:var(--inv-text-muted)]"
                }`}
              >
                {s.description}
              </p>
            </div>
          ))}
        </div>

        {/* Week-by-week view */}
        <div className="mt-16">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight mb-6">
            Próximas 4 semanas
          </h2>
          <div className="space-y-6">
            {weeks.map((week, idx) => (
              <div
                key={idx}
                data-testid={`week-${idx}`}
                className="bg-surface border border-warm rounded-2xl overflow-hidden"
              >
                <div className="px-6 py-4 bg-surface-alt border-b border-warm flex items-center gap-2">
                  <Calendar size={16} className="text-[color:var(--inv-primary)]" />
                  <span className="font-medium text-sm">
                    Semana del{" "}
                    {week.start.toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "long",
                    })}
                  </span>
                </div>
                <ul className="divide-y divide-[color:var(--inv-border)]">
                  {week.items.map((it, i) => (
                    <li
                      key={`${it.id}-${i}`}
                      className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6"
                    >
                      <div className="md:w-44 shrink-0">
                        <div className="font-medium capitalize">
                          {it.weekdayLabel}
                        </div>
                        <div className="text-xs text-[color:var(--inv-text-muted)]">
                          {formatDate(it.date)}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-heading text-lg font-semibold tracking-tight">
                            {it.title}
                          </span>
                          {it.kind === "special" && (
                            <span className="text-[10px] uppercase tracking-widest bg-[color:var(--inv-primary)] text-white px-2 py-0.5 rounded-full font-semibold">
                              Evento especial
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-[color:var(--inv-text-muted)] flex flex-wrap gap-4">
                          <span className="inline-flex items-center gap-1.5">
                            <Clock size={13} /> {it.timeLabel}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin size={13} /> {it.location}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
